import type { CouponDiscountKind } from '#graphql-operations'

/** A code already on the order, as the shop API describes it (`couponPreview`). */
export interface AppliedCouponKind {
  code: string
  /** Redeemable right now. A code that is not takes nothing off the order. */
  valid: boolean
  /**
   * Fixed amount or percentage, or null for any other promotion: a gift rule, free
   * shipping, a mix of actions.
   */
  kind: CouponDiscountKind | null
}

/**
 * The codes already on the order that `incoming` takes the place of.
 *
 * An order holds one fixed-amount and one percentage code: `ApplyCouponCodeResolver`
 * in the Vendure app (exalted-order-validation-plugin) refuses anything else with
 * COUPON_CODE_COMBINATION_ERROR. The storefront never answers a code the customer
 * has just brought — an affiliate link, a code typed at checkout — with that
 * refusal: the code it cannot sit beside makes room for it instead, whatever that
 * code was worth. This is the storefront's copy of the backend's
 * `conflictingCouponCodes`, so the two agree on which codes those are: a code of
 * the incoming one's own kind, and every code when either side has no kind. A code
 * that is no longer redeemable goes too: it discounts nothing, and a withdrawn
 * promotion has no kind on the server either, so nothing could be seated beside it.
 *
 * A code of the other kind is not in the answer: it stays on the order next to the
 * incoming one.
 *
 * Pure, so it can be tested; `codesDisplacedBy` in `useUrlCoupon` asks the shop API
 * about each code first.
 */
export function couponCodesDisplacedBy(
  existing: readonly AppliedCouponKind[],
  incoming: { code: string, kind: CouponDiscountKind | null },
): string[] {
  const incomingCode = incoming.code.trim().toUpperCase()
  return existing
    .filter(applied => applied.code.trim().toUpperCase() !== incomingCode)
    .filter(applied => !applied.valid || !incoming.kind || !applied.kind || applied.kind === incoming.kind)
    .map(applied => applied.code)
}
